// Auto-guardado de líneas sin enviar — ejecución (store + petición).
//
// La decisión (qué líneas, cuándo, guardas) está en autoSaveDraftCore. Aquí
// se guarda con la MISMA petición que el botón «Guardar»
// (utils/tpv/comandaRequest, servido=false) en dos variantes:
//
//   source "cart"  → las líneas están en el carrito en pantalla (60 s sin
//                    tocar nada, app a segundo plano, o se sale a otra
//                    sección con el carrito aún cargado): se marcan enviadas
//                    en el carrito (optimista, como «Guardar»).
//   source "draft" → la mesa ya no está en el carrito (se cambió de mesa):
//                    las líneas salen del borrador local de esa mesa.
//   `leaving`      → la mesa se acaba de dejar: la petición lleva el lease
//                    de ESA mesa y un 409 suyo no expulsa de la mesa actual.
//
// Si falla (sin conexión…) todo vuelve al borrador local, como hoy.

import { uuid } from "src/utils/requestDedupe";
import { LEASE_HEADER } from "src/utils/mesaLeaseCore";
import {
  SyncCart,
  addCartMany,
  optimisticComandarCommit,
  optimisticComandarRollback,
  optimisticComandarStart,
  saveHistoryCheckout,
  setHistoryDraftCart,
} from "src/redux/slices/product";
import { updateMesaSuccess } from "src/redux/slices/mesas";
import { refreshMesaOrders } from "src/utils/mesaCheckoutLoader";
import { activeMesaId } from "src/redux/middleware/cartDraftMiddleware";
import { comandaSessionId } from "src/utils/tpv/resyncAfterComanda";
import journal from "src/utils/journal/journal";
import { cartTotals } from "src/utils/journal/journalCore";
import { buildComandaData, buildComandaLines, postComanda } from "src/utils/tpv/comandaRequest";
import { reconcileMesaCuentaAfterComanda } from "src/utils/tpv/comandaReconcile";
import { clearCheckPrintedOnComanda } from "src/utils/tpv/checkPrintedLocal";

const inFlight = new Set();

export const isAutoSaveInFlight = (mesaId) => inFlight.has(String(mesaId));

const sameId = (a, b) => a != null && b != null && String(a) === String(b);

function restaurantId(mesa) {
  let rest = null;
  try {
    rest = window.localStorage.getItem("userId");
  } catch {
    rest = null;
  }
  return rest == null || rest === "" ? mesa?.idRestaurante : rest;
}

function draftCartOf(getState, mesaId) {
  const entry = (getState()?.product?.checkoutHistory || []).find((m) => sameId(m.idMesa, mesaId));
  return entry?.checkout?.cart || [];
}

/**
 * Guarda `lines` de `mesa`. `keep` = lo que se queda en el borrador (menús
 * incompletos). `leaseId` = lease de la mesa en el momento de decidir.
 * Resuelve `{ ok, count }`; nunca lanza.
 */
export async function runAutoSave({
  dispatch,
  getState,
  mesa,
  lines,
  keep = [],
  source,
  leaving = false,
  leaseId = null,
  onSuccess,
  onFailure,
}) {
  const mesaId = mesa?.id;
  if (!mesaId || !lines?.length) return { ok: false, count: 0 };
  const key = String(mesaId);
  if (inFlight.has(key)) return { ok: false, count: 0 };
  inFlight.add(key);

  const state = getState();
  const rest = restaurantId(mesa);
  const idSession = mesa.sessionId;
  const productos = buildComandaLines(lines, { idOrder: state?.product?.checkout?.idOrder ?? null });
  const data = buildComandaData({
    productos,
    idRestaurante: rest,
    idSession,
    idMesa: mesaId,
    servido: false, // = «Guardar»: KDS, sin papel
    idEmployee: state?.employees?.currentActiveEmployee || 0,
  });

  const optimisticKey = uuid();
  if (source === "cart") {
    dispatch(optimisticComandarStart({ optimisticKey, servido: false, skipPartial: true }));
    // El borrador se queda solo con lo que no se ha guardado.
    dispatch(saveHistoryCheckout(mesaId));
  } else {
    dispatch(setHistoryDraftCart({ idMesa: mesaId, cart: keep }));
  }

  // Cuenta de la mesa (plano), como hace «Guardar».
  const listMesa =
    (getState()?.mesas?.mesas || []).find((m) => sameId(m?.id, mesaId)) ||
    (sameId(getState()?.mesas?.mesa?.id, mesaId) ? getState().mesas.mesa : null);
  if (listMesa) {
    dispatch(updateMesaSuccess({ ...listMesa, cuenta: (Number(listMesa.cuenta) || 0) + data.precio }));
  }
  // Otra ronda guardada: la cuenta impresa ya no vale (el servidor también la resetea).
  dispatch(clearCheckPrintedOnComanda(mesaId));

  journal.track("comandar.autosave", cartTotals(productos), { mesaId, sessionId: idSession });

  const config = {
    ...(leaseId ? { headers: { [LEASE_HEADER]: leaseId } } : {}),
    ...(leaving ? { leaseMesaId: mesaId, skipLeaseHeldNotify: true } : {}),
  };

  try {
    const response = await postComanda(data, config);
    if (listMesa) dispatch(reconcileMesaCuentaAfterComanda(mesaId, data.precio, response));
    if (source === "cart") dispatch(optimisticComandarCommit({ optimisticKey }));
    // Si la mesa está en pantalla (o se ha vuelto a ella), sus líneas con los
    // ids del servidor y pedidos frescos para el cobro.
    if (sameId(activeMesaId(getState()), mesaId)) {
      const sid = comandaSessionId(response, idSession);
      if (sid) dispatch(SyncCart(mesaId, sid));
      refreshMesaOrders(dispatch, rest, mesaId);
    }
    onSuccess?.(lines.length);
    return { ok: true, count: lines.length };
  } catch (error) {
    // Vuelta al borrador local (comportamiento de siempre sin conexión).
    if (source === "cart") {
      dispatch(optimisticComandarRollback({ optimisticKey }));
    }
    if (sameId(activeMesaId(getState()), mesaId)) {
      if (source === "draft") dispatch(addCartMany(lines));
      dispatch(saveHistoryCheckout(mesaId));
    } else {
      dispatch(setHistoryDraftCart({ idMesa: mesaId, cart: [...lines, ...draftCartOf(getState, mesaId)] }));
    }
    if (listMesa) {
      const now = (getState()?.mesas?.mesas || []).find((m) => sameId(m?.id, mesaId)) || listMesa;
      dispatch(updateMesaSuccess({ ...now, cuenta: Math.max(0, (Number(now.cuenta) || 0) - data.precio) }));
    }
    onFailure?.(error);
    return { ok: false, count: 0, error };
  } finally {
    inFlight.delete(key);
  }
}

export function __resetAutoSaveRunnerForTests() {
  inFlight.clear();
}
